<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { WithTestimonialProps } from "./WithTestimonialProps";
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import FormField from "../components/form-field/FormField.vue";
import FormMessages from "../components/form-messages/FormMessages.vue";
import SubmitButton from "../components/submit-button/SubmitButton.vue";
import PrivacyPolicy from "../components/privacy-policy/PrivacyPolicy.vue";
import { useContactForm } from "../common/useContactForm";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";

const props = defineProps<{
    block: PageBlock<WithTestimonialProps>;
    pageColors: CssVarColors;
    pageUuid: string;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const { agreed, formSubmitted, isSubmitting, showSuccessMessage, showErrorMessage, handleSubmit } =
    useContactForm({
        // hookUrl: dataValues.value.submitButton.data.hookUrl,
        privacyPolicyHidden: dataValues.value.privacyPolicy.isHidden,
        validationMessage: dataValues.value.privacyPolicy.data.validationMessage,
        successMessage: dataValues.value.submitButton.data.successMessage,
        errorMessage: dataValues.value.submitButton.data.errorMessage,
        pageUuid: props.pageUuid,
        sectionId: props.block.id,
    });
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing class="pattern">
            <div class="mx-auto max-w-xl lg:max-w-5xl">
                <!-- Title and Description -->
                <EditableElement
                    v-if="!dataValues.title.isHidden"
                    :blockId="block.id"
                    field-name="title"
                >
                    <Heading
                        :text="dataValues.title.data.text"
                        :tag="dataValues.title.data.tag"
                        :textColor="styleValues.text"
                        class="text-4xl sm:text-5xl mb-6"
                    />
                </EditableElement>
                <EditableElement
                    v-if="!dataValues.description.isHidden"
                    :blockId="block.id"
                    field-name="description"
                >
                    <Paragraph
                        :text="dataValues.description.data.text"
                        :textColor="styleValues.text"
                        class="text-lg/8 opacity-70"
                    />
                </EditableElement>

                <!-- Main Content Grid -->
                <div class="grid grid-cols-1 lg:grid-cols-8 gap-16 sm:gap-20 mt-16">
                    <!-- Form Section -->
                    <div class="lg:col-span-5">
                        <form @submit="handleSubmit">
                            <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                                <EditableElement
                                    v-for="field in dataValues.formFields.data.items"
                                    :key="field.id"
                                    :blockId="block.id"
                                    :field-name="`formFields_${field.id}`"
                                    :hideable="false"
                                >
                                    <FormField
                                        v-bind="field"
                                        :label-color="styleValues.text"
                                        :accent-color="styleValues.brand"
                                        :border-color="styleValues.border"
                                    />
                                </EditableElement>
                                <EditableElement
                                    v-if="!dataValues.privacyPolicy.isHidden"
                                    :blockId="block.id"
                                    field-name="privacyPolicy"
                                >
                                    <PrivacyPolicy
                                        v-bind="dataValues.privacyPolicy.data"
                                        :accent-color="styleValues.brand"
                                        v-model:agreed="agreed"
                                    />
                                </EditableElement>
                            </div>
                            <div class="mt-6 space-y-4">
                                <EditableElement
                                    v-if="!dataValues.submitButton.isHidden"
                                    :blockId="block.id"
                                    field-name="submitButton"
                                >
                                    <SubmitButton
                                        :label="dataValues.submitButton.data.label"
                                        :text-color="'#ffffff'"
                                        :background-color="styleValues.brand"
                                        :is-submitting="isSubmitting"
                                    />
                                </EditableElement>
                            </div>
                            <FormMessages
                                :formSubmitted="formSubmitted"
                                :agreed="agreed"
                                :validationMessage="dataValues.privacyPolicy.data.validationMessage"
                                :successMessage="dataValues.submitButton.data.successMessage"
                                :errorMessage="dataValues.submitButton.data.errorMessage"
                                :showSuccessMessage="showSuccessMessage"
                                :showErrorMessage="showErrorMessage"
                            />
                        </form>
                    </div>

                    <!-- Testimonial Section -->
                    <div class="flex flex-col gap-10 lg:col-span-3">
                        <EditableElement
                            v-if="!dataValues.testimonial.isHidden"
                            :blockId="block.id"
                            field-name="testimonial"
                        >
                            <Image
                                :src="dataValues.testimonial.data.logoImage"
                                alt="Company logo"
                                width="189"
                                height="48"
                                class="max-h-[48px] object-cover"
                            />
                            <Paragraph
                                :text="dataValues.testimonial.data.text"
                                :textColor="styleValues.text"
                                class="text-lg/8 italic"
                            />
                            <div class="flex items-center gap-6">
                                <img
                                    :src="dataValues.testimonial.data.personImage"
                                    :alt="dataValues.testimonial.data.personName"
                                    class="w-12 h-12 rounded-full object-cover"
                                    width="48"
                                    height="48"
                                />
                                <div>
                                    <Paragraph
                                        :text="dataValues.testimonial.data.personName"
                                        :textColor="styleValues.text"
                                        class="font-semibold"
                                    />
                                    <Paragraph
                                        :text="dataValues.testimonial.data.personPosition"
                                        :textColor="styleValues.text"
                                        class="text-sm/6 opacity-70"
                                    />
                                </div>
                            </div>
                        </EditableElement>
                    </div>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>

<style scoped>
.pattern {
    background-color: v-bind("styleValues.background");
    background-image: url("data:image/svg+xml,%3Csvg width='500' height='500' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v20h2v2H20v-1.5zM0 20h2v20H0V20zm4 0h2v20H4V20zm4 0h2v20H8V20zm4 0h2v20h-2V20zm4 0h2v20h-2V20zm4 4h20v2H20v-2zm0 4h20v2H20v-2zm0 4h20v2H20v-2zm0 4h20v2H20v-2z' fill='%239C92AC' fill-opacity='0.02' fill-rule='evenodd'/%3E%3C/svg%3E");
}
</style>
